import { addTicket, selectActiveTicket, updateTicket } from '@/redux/data/dataSlice';
import dayjs from 'dayjs';
import { cloneDeep, filter, findIndex, map, size } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  DiscountType,
  ItemType,
  ModifierOptionType,
  TicketItemType,
} from '../../redux/data/dataTypes';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

type ticketType = 'PAY' | 'REFUND';
interface MyHookProps {
  type: ticketType;
}
const useTicket = (initialValue: MyHookProps = { type: 'PAY' }) => {
  const activeTicket = useAppSelector(selectActiveTicket);
  // const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const addItemToTicket = (item: ItemType) => {
    if (!item.price) return;
    const saleItem: TicketItemType = {
      ID: item.ID,
      name: item.name,
      nameAr: item.nameAr,
      SKU: item.SKU,
      sellingType: item.sellingType,
      price: item.priceAfterTax ?? item.price,
      cost: item.cost,
      categoryId: item.categoryId,
      quantity: item?.quantity ?? 1,
    };
    if (!activeTicket) {
      dispatch(
        addTicket({
          ID: uuidv4(),
          status: 'ACTIVE',
          category: 'IN',
          type: initialValue.type,
          createdAt: dayjs().toISOString(),
          items: [saleItem],
          savedToBackend: false,
        })
      );
    } else {
      const clonedTicket = cloneDeep(activeTicket);
      // clonedTicket.items = [];
      // clonedTicket.discounts=[]
      const checkItem = findIndex(clonedTicket.items, (d) => d?.ID == item.ID);
      if (checkItem > -1 && clonedTicket.items) {
        clonedTicket.items[checkItem].quantity += item?.quantity ?? 1;
      } else {
        clonedTicket.items = [...(clonedTicket.items ?? []), saleItem];
      }
      dispatch(updateTicket(clonedTicket));
    }
  };
  const addItem = (item: ItemType) => {
    if (!item.price) return;
    // TODO: add it back later with an option on the item to show the alert
    // if (item.stock != null && item.stock <= 0) {
    //   return new Promise((resolve, reject) => {
    //     Modal.confirm({
    //       title: t('common:negative_stock_alert_title'),
    //       content: t('common:negative_stock_alert_desc'),
    //       okText: t('common:continue'),
    //       cancelText: t('common:cancel'),
    //       centered: true,
    //       onOk: () => {
    //         addItemToTicket(item);
    //         resolve(true);
    //       },
    //       onCancel: () => {
    //         reject('negative_stock');
    //       },
    //     });
    //   });
    // }
    addItemToTicket(item);
  };

  const updateItem = (itemID: string, updateData: any) => {
    const clonedTicket = cloneDeep(activeTicket);
    const itemIdx = findIndex(clonedTicket?.items, (d) => d.ID == itemID);
    // const orginalItem = find(store.getState().data.items, (d) => d.ID == itemID);
    if (clonedTicket?.items && itemIdx > -1) {
      clonedTicket.items[itemIdx] = {
        ...clonedTicket?.items[itemIdx],
        ...updateData,
      };
      // TODO: add it back later with an option on the item to show the alert
      // if (
      //   orginalItem &&
      //   orginalItem.stock != null &&
      //   orginalItem.stock - clonedTicket.items[itemIdx].quantity < 0
      // ) {
      //   return new Promise((resolve, reject) => {
      //     Modal.confirm({
      //       title: t('common:negative_stock_alert_title'),
      //       content: t('common:negative_stock_alert_desc'),
      //       okText: t('common:continue'),
      //       cancelText: t('common:cancel'),
      //       onOk: () => {
      //         dispatch(updateTicket(clonedTicket));
      //         resolve(true);
      //       },
      //       onCancel: () => {
      //         reject('negative_stock');
      //       },
      //     });
      //   });
      // }
      dispatch(updateTicket(clonedTicket));
    }
  };

  const updateModifierOption = (optionID: string, updateData: any) => {
    const clonedTicket = cloneDeep(activeTicket);
    const optionIdx = findIndex(clonedTicket?.modifierOptions, (d) => d.ID == optionID);
    if (clonedTicket?.modifierOptions && optionIdx > -1) {
      clonedTicket.modifierOptions[optionIdx] = {
        ...clonedTicket?.modifierOptions[optionIdx],
        ...updateData,
      };
      dispatch(updateTicket(clonedTicket));
    }
  };

  const addDiscount = (discount: DiscountType) => {
    return new Promise((resolve, reject) => {
      if (!discount?.value) return;
      if (!activeTicket) return reject('NO_ACTIVE_TICKET_DISCOUNT');
      // not items items exist
      const clonedTicket = cloneDeep(activeTicket);
      const checkItem = findIndex(clonedTicket.discounts, (d) => d?.ID == discount.ID);
      if (checkItem > -1 && clonedTicket.items) return reject('DISCOUNT_ALREADY_USED');

      clonedTicket.discounts = [
        ...(clonedTicket.discounts ?? []),
        {
          ID: discount.ID,
          value: discount.value,
          type: discount.type,
          name: discount.name,
        },
      ];
      dispatch(updateTicket(clonedTicket));
    });
  };
  const removeDiscount = (discount: DiscountType) => {
    return new Promise((resolve, reject) => {
      if (!discount?.ID) return;
      if (!activeTicket) return reject('NO_ACTIVE_TICKET');
      // not items items exist
      const clonedTicket = cloneDeep(activeTicket);
      clonedTicket.discounts = filter(clonedTicket.discounts, (d) => d.ID != discount?.ID);
      dispatch(updateTicket(clonedTicket));
    });
  };

  const addModifierOptions = (options: ModifierOptionType[]) => {
    if (!activeTicket) {
      dispatch(
        addTicket({
          ID: uuidv4(),
          status: 'ACTIVE',
          createdAt: dayjs().toISOString(),
          type: initialValue.type,
          modifierOptions: map(options, (d) => {
            return {
              ID: d.ID,
              name: d.name,
              nameAr: d.nameAr,
              price: d.price,
              quantity: 1,
            };
          }),
          savedToBackend: false,
        })
      );
    } else {
      const clonedTicket = cloneDeep(activeTicket);
      if (!clonedTicket.modifierOptions || size(clonedTicket.modifierOptions) == 0) {
        clonedTicket.modifierOptions = map(options, (d) => {
          return {
            ID: d.ID,
            name: d.name,
            nameAr: d.nameAr,
            price: d.price,
            quantity: 1,
          };
        });
      } else {
        options.forEach((option) => {
          const checkOptionsIndex = findIndex(
            clonedTicket.modifierOptions,
            (d) => d.ID == option.ID
          );
          if (checkOptionsIndex > -1 && clonedTicket.modifierOptions) {
            clonedTicket.modifierOptions[checkOptionsIndex].quantity += 1;
          } else {
            clonedTicket.modifierOptions = [
              ...(clonedTicket.modifierOptions ?? []),
              {
                ID: option.ID,
                name: option.name,
                nameAr: option.nameAr,
                price: option.price,
                quantity: 1,
              },
            ];
          }
        });
      }
      dispatch(updateTicket(clonedTicket));
    }
  };

  return {
    addItem,
    updateItem,
    updateModifierOption,
    addDiscount,
    removeDiscount,
    addModifierOptions,
  };
};

export default useTicket;
